import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DailyIframe from '@daily-co/daily-js';
import type { DailyCall, DailyEvent } from '@daily-co/daily-js';
import { LoadingScreen } from './LoadingScreen';

interface WebinarRoomProps {
  roomUrl: string;
}

export function WebinarRoom({ roomUrl }: WebinarRoomProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const callFrameRef = useRef<DailyCall | null>(null);
  const isCreatingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!roomUrl) {
      setError(t('webinar.noRoomUrl', 'No room URL provided'));
      return;
    }

    // Prevent duplicate creation attempts
    if (isCreatingRef.current || callFrameRef.current) {
      console.log('Daily frame already exists or is being created, skipping');
      return;
    }

    isCreatingRef.current = true;

    // Ensure no existing frames before creating new one
    const destroyExistingFrame = async () => {
      if (callFrameRef.current) {
        try {
          await callFrameRef.current.destroy();
          callFrameRef.current = null;
          console.log('Destroyed existing Daily frame');
        } catch (err) {
          console.error('Error destroying existing frame:', err);
        }
      }
    };

    const initializeFrame = async () => {
      try {
        await destroyExistingFrame();

        // Create Daily iframe
        const callFrame = DailyIframe.createFrame({
          iframeStyle: {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            border: '0',
          },
          showLeaveButton: true,
          showFullscreenButton: true,
        });

        callFrameRef.current = callFrame;
        isCreatingRef.current = false;

        // Set up event handlers
        callFrame.on('loaded', () => {
          console.log('Daily iframe loaded');
          setIsLoading(false);
        });

        callFrame.on('joined-meeting', (event?: DailyEvent) => {
          console.log('Joined webinar meeting', event);
        });

        callFrame.on('left-meeting', (event?: DailyEvent) => {
          console.log('Left webinar meeting', event);
          // Navigate back to courses page
          navigate('/courses');
        });

        callFrame.on('error', (event?: DailyEvent) => {
          console.error('Daily error:', event);
          setError(t('webinar.connectionError', 'Connection error occurred'));
          setIsLoading(false);
        });

        // Join the meeting
        await callFrame.join({ url: roomUrl });
        console.log('Successfully joined webinar');
      } catch (err) {
        console.error('Error creating Daily frame:', err);
        isCreatingRef.current = false;
        setError(t('webinar.initError', 'Failed to initialize webinar'));
        setIsLoading(false);
      }
    };

    initializeFrame();

    // Cleanup function
    return () => {
      isCreatingRef.current = false;
      if (callFrameRef.current) {
        callFrameRef.current
          .destroy()
          .then(() => {
            console.log('Daily frame destroyed');
            callFrameRef.current = null;
          })
          .catch((err: Error) => {
            console.error('Error destroying Daily frame:', err);
            callFrameRef.current = null;
          });
      }
    };
  }, [roomUrl, navigate, t]);

  if (error) {
    return (
      <div className="fixed inset-0 bg-brand-dark flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 max-w-md mx-4 text-center">
          <h2 className="text-2xl font-bold text-brand-dark mb-4">
            {t('webinar.error', 'Error')}
          </h2>
          <p className="text-brand-dark/70 mb-6">{error}</p>
          <button
            onClick={() => navigate('/courses')}
            className="bg-brand-terracotta hover:bg-brand-terracotta/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {t('webinar.backToCourses', 'Back to Courses')}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-brand-dark">
        <LoadingScreen />
      </div>
    );
  }

  return <div id="daily-webinar-frame" />;
}
